import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';

type Variant = 'success' | 'cancelled' | 'error';

function useReturnParams() {
  const [searchParams] = useSearchParams();
  return useMemo(
    () => ({
      transactionId:
        searchParams.get('transaction_id') ||
        searchParams.get('transactionId') ||
        '',
      status: searchParams.get('status') || '',
      message: searchParams.get('message') || '',
    }),
    [searchParams],
  );
}

function Content({ variant }: { variant: Variant }) {
  const { transactionId, status, message } = useReturnParams();

  if (variant === 'success') {
    const completed = status.toLowerCase() === 'completed';
    const pending = status.toLowerCase() === 'pending' || !status;

    return (
      <>
        <h1 className="text-xl font-bold text-gray-900">Payment return</h1>
        {transactionId ? (
          <p className="mt-2 font-mono text-sm text-gray-600 break-all">Order: {transactionId}</p>
        ) : null}
        <p className="mt-4 text-gray-700">
          {completed
            ? 'Your payment was recorded as completed. Your credit balance should update shortly.'
            : pending
              ? 'If you just finished checkout, the payment may still be confirming. Refresh your overview in a moment or open Credit transactions for ledger entries.'
              : `Status from provider redirect: ${status || 'unknown'}. Check Credit transactions or try again if something looks wrong.`}
        </p>
      </>
    );
  }

  if (variant === 'cancelled') {
    return (
      <>
        <h1 className="text-xl font-bold text-gray-900">Payment cancelled</h1>
        {transactionId ? (
          <p className="mt-2 font-mono text-sm text-gray-600 break-all">Order: {transactionId}</p>
        ) : null}
        <p className="mt-4 text-gray-700">No charge was completed. You can start a new purchase anytime.</p>
      </>
    );
  }

  return (
    <>
      <h1 className="text-xl font-bold text-gray-900">Payment error</h1>
      <p className="mt-4 text-red-700">{message || 'Something went wrong processing the return link.'}</p>
    </>
  );
}

export function AgentPaymentSuccessPage() {
  return (
    <AppLayout>
      <Content variant="success" />
      <FooterLinks />
    </AppLayout>
  );
}

export function AgentPaymentCancelledPage() {
  return (
    <AppLayout>
      <Content variant="cancelled" />
      <FooterLinks />
    </AppLayout>
  );
}

export function AgentPaymentErrorPage() {
  return (
    <AppLayout>
      <Content variant="error" />
      <FooterLinks />
    </AppLayout>
  );
}

function FooterLinks() {
  return (
    <div className="mt-8 flex flex-wrap gap-4 text-sm font-semibold">
      <Link to="/dashboard" className="text-orange-700 hover:text-orange-900">
        Back to Overview
      </Link>
      <Link to="/transactions" className="text-orange-700 hover:text-orange-900">
        View transactions
      </Link>
    </div>
  );
}

/** Single route for `/payment/return`; picks view from query params. */
export default function AgentPaymentReturnPage() {
  const [searchParams] = useSearchParams();
  const status = (searchParams.get('status') || '').toLowerCase();
  const variant = searchParams.get('variant') || '';
  if (variant === 'error' || searchParams.get('error') === '1') {
    return <AgentPaymentErrorPage />;
  }
  if (variant === 'cancelled' || status === 'cancelled' || status === 'canceled') {
    return <AgentPaymentCancelledPage />;
  }
  return <AgentPaymentSuccessPage />;
}
